import { useCallback, useMemo, useRef, useState } from 'react';
import {
    IconContrast,
    IconCrop,
    IconDownload,
    IconFileTypePdf,
    IconLayoutColumns,
    IconPalette,
    IconPrinter,
    IconRefresh,
    IconRotateClockwise,
    IconSparkles,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { encodeCanvas } from '../shared/imageOutputFormats';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import SettingSlider from '../shared/SettingSlider';
import ApplyToAllButton from '../shared/ApplyToAllButton';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import DocumentCleanupCropModal from './DocumentCleanupCropModal';
import DocumentCleanupPreview from './DocumentCleanupPreview';
import DocumentCleanupPageRail from './DocumentCleanupPageRail';
import useDocumentCleanupPages from './useDocumentCleanupPages';
import { printImageDataUrl } from './printDocumentImage';
import { buildPagesPdf } from './pagesPdf';
import {
    ACCEPT,
    CLEAN_PRESETS,
    DEFAULT_ADJUSTMENTS,
    imageDataToCanvas,
    imageDataToDataUrl,
    slidersForPreset,
} from './documentCleanupEngine';
import { MAX_PAGES } from './documentCleanupPages';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

/** PDFs join the picker now that a PDF drops in as one page per page. */
const PICKER_ACCEPT = `${ACCEPT},application/pdf,.pdf`;

function segBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const PRESET_ICONS = {
    normal: IconWand,
    colorClean: IconSparkles,
    colorScan: IconPalette,
    bw: IconContrast,
};

export default function DocumentCleanupImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [format, setFormat] = useState('jpg');
    const [cropOpen, setCropOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [error, setError] = useState(null);

    const reportError = useCallback((reason) => {
        const message = t(`documentCleanupImage.errors.${reason}`, {
            defaultValue: t('documentCleanupImage.errors.failed'),
        });
        setError(message);
        notifications.show({ title: t('common.error'), message, color: 'red' });
    }, [t]);

    const {
        pages,
        selected,
        selectedId,
        select,
        addFiles,
        removePage,
        reorder,
        clear,
        previewResult,
        previewSource,
        baseUrl,
        sourceSize,
        busy,
        importing,
        compare,
        setCompare,
        setPreset,
        setAdjustment,
        resetAdjustments,
        applyToAll,
        rotate,
        applyCrop,
        renderPageFull,
    } = useDocumentCleanupPages({ onError: reportError });

    const working = busy || importing || exporting;

    const handleAdd = useCallback((incoming) => {
        setError(null);
        setDownloaded(false);
        void addFiles(incoming);
    }, [addFiles]);

    // Ctrl/Cmd+V anywhere on the page drops copied pictures straight in.
    useImagePaste(handleAdd, { enabled: !working });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleAdd(e.dataTransfer.files);
    };

    const openPicker = () => fileInputRef.current?.click();

    const handleReset = useCallback(() => {
        clear();
        setCropOpen(false);
        setDownloaded(false);
        setError(null);
    }, [clear]);

    /** Every page, cleaned at full resolution, in rail order. */
    const renderAll = useCallback(async () => {
        const out = [];
        for (const page of pages) {
            const result = await renderPageFull(page.id);
            if (result) out.push({ page, result });
        }
        return out;
    }, [pages, renderPageFull]);

    const runExport = useCallback(async (job) => {
        if (!pages.length || working) return;
        setExporting(true);
        try {
            recordToolUsage('image/document-cleanup', { category: 'image' });
            await job();
            setDownloaded(true);
            setError(null);
            notifications.show({
                title: t('common.success'),
                message: t('documentCleanupImage.done'),
                color: 'green',
            });
        } catch {
            reportError('failed');
        } finally {
            setExporting(false);
        }
    }, [pages.length, reportError, t, working]);

    /** Images out: one file when there is one page, a ZIP when there are more. */
    const handleDownload = useCallback(() => runExport(async () => {
        const rendered = await renderAll();
        if (!rendered.length) return;

        const files = [];
        for (const { page, result } of rendered) {
            const { blob, ext } = await encodeCanvas(imageDataToCanvas(result), format, { quality: 0.95 });
            const base = (page.name || 'document').replace(/\.[^.]+$/, '');
            files.push({ blob, name: `${base}-cleaned.${ext}` });
        }

        if (files.length === 1) {
            downloadBlob(files[0].blob, files[0].name);
            return;
        }
        downloadBlob(await zipImageFiles(files), 'cleaned-documents.zip');
    }), [format, renderAll, runExport]);

    /** The whole job as one PDF, a page per sheet (see pagesPdf.js). */
    const handleDownloadPdf = useCallback(() => runExport(async () => {
        const rendered = await renderAll();
        if (!rendered.length) return;
        downloadBlob(await buildPagesPdf(rendered.map(({ result }) => result)), 'cleaned-documents.pdf');
    }), [renderAll, runExport]);

    const handlePrint = useCallback(() => {
        if (!pages.length || working) return;
        recordToolUsage('image/document-cleanup', { category: 'image' });
        setExporting(true);
        void (async () => {
            try {
                const rendered = await renderAll();
                if (!rendered.length) return;
                await printImageDataUrl(
                    rendered.map(({ result }) => imageDataToDataUrl(result, 'image/jpeg', 0.95)),
                    t('documentCleanupImage.print.title'),
                );
            } catch {
                reportError('failed');
            } finally {
                setExporting(false);
            }
        })();
    }, [pages.length, renderAll, reportError, t, working]);

    const hasPages = pages.length > 0;
    const preset = selected?.preset ?? 'none';
    const adjustments = selected?.adjustments ?? DEFAULT_ADJUSTMENTS;
    const visibleSliders = useMemo(() => slidersForPreset(preset), [preset]);
    const pageIndex = pages.findIndex((p) => p.id === selectedId);

    /** Hover toolbar over the preview image — acts on the SELECTED page. */
    const previewActions = useMemo(() => [
        {
            id: 'crop',
            icon: IconCrop,
            label: t('documentCleanupImage.manualCrop'),
            onClick: () => { setCompare(false); setCropOpen(true); },
            disabled: working || !selected,
        },
        {
            id: 'rotate',
            icon: IconRotateClockwise,
            label: t('documentCleanupImage.rotate'),
            onClick: rotate,
            disabled: working || !selected,
        },
        {
            id: 'compare',
            // A split-screen glyph, not an eye: the button no longer swaps the
            // picture for the original, it puts the two either side of a handle.
            icon: IconLayoutColumns,
            label: t('documentCleanupImage.compare'),
            onClick: () => setCompare((v) => !v),
            active: compare,
            disabled: working || !previewResult || !previewSource,
        },
    ], [compare, previewResult, rotate, selected, setCompare, t, working]);

    const status = deriveToolStatus({
        hasInput: hasPages,
        canRun: !!previewResult,
        processing: working,
        hasResult: downloaded,
        hasError: !!error,
    });

    const statusBits = [getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('documentCleanupImage.done'),
    })];
    if (hasPages) {
        statusBits.push(t('documentCleanupImage.pageCount', { count: toBnDigits(pages.length) }));
    }
    if (sourceSize) {
        statusBits.push(`${toBnDigits(sourceSize.width)}×${toBnDigits(sourceSize.height)}`);
    }

    return (
        <>
            <ToolPageLayout
                slug="image/document-cleanup"
                subtitleKey="documentCleanupImage.subtitle"
                status={status}
                statusText={statusBits.join(' · ')}
                onReset={hasPages || error ? handleReset : undefined}
                primaryAction={{
                    label: pages.length > 1
                        ? t('documentCleanupImage.downloadAll', { count: toBnDigits(pages.length) })
                        : t('documentCleanupImage.download'),
                    onClick: handleDownload,
                    disabled: !hasPages || working,
                    loading: exporting,
                    icon: IconDownload,
                }}
                secondaryAction={[
                    {
                        key: 'pdf',
                        label: t('documentCleanupImage.downloadPdf'),
                        onClick: handleDownloadPdf,
                        disabled: !hasPages || working,
                        icon: IconFileTypePdf,
                    },
                    {
                        key: 'print',
                        label: t('documentCleanupImage.print.action'),
                        onClick: handlePrint,
                        disabled: !hasPages || working,
                        icon: IconPrinter,
                    },
                ]}
                showTrust={false}
                settings={(
                    <div className="flex flex-col gap-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={PICKER_ACCEPT}
                            multiple
                            className="hidden"
                            onChange={(e) => { handleAdd(e.target.files); e.target.value = ''; }}
                        />

                        <ToolCard title={t('documentCleanupImage.presetTitle')}>
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label, so all four fit on one row. */}
                            <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {CLEAN_PRESETS.map((id) => {
                                    const Icon = PRESET_ICONS[id];
                                    const label = t(`documentCleanupImage.presets.${id}`);
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            disabled={working || !selected}
                                            onClick={() => {
                                                setPreset(preset === id ? 'none' : id);
                                                setDownloaded(false);
                                            }}
                                            className={segBtnClass(preset === id)}
                                            title={label}
                                            aria-label={label}
                                            aria-pressed={preset === id}
                                        >
                                            <Icon size={20} stroke={1.8} />
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                                {preset === 'none'
                                    ? t('documentCleanupImage.presetHints.none')
                                    : t(`documentCleanupImage.presetHints.${preset}`)}
                            </p>
                        </ToolCard>

                        {/* Sliders only mean something once there is a page to
                            tune, so the card stays out of the way until then. */}
                        {selected ? (
                            <ToolCard
                                title={pageIndex >= 0
                                    ? t('documentCleanupImage.adjustTitleFor', {
                                        page: toBnDigits(pageIndex + 1),
                                    })
                                    : t('documentCleanupImage.adjustTitle')}
                                headerAction={(
                                    <button
                                        type="button"
                                        onClick={resetAdjustments}
                                        disabled={working}
                                        title={t('documentCleanupImage.resetSliders')}
                                        aria-label={t('documentCleanupImage.resetSliders')}
                                        className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-[11.5px] font-semibold text-[#6B635C] transition-colors hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-45 dark:text-zinc-400 dark:hover:text-amber-400"
                                    >
                                        <IconRefresh size={14} stroke={2} />
                                        {t('documentCleanupImage.resetSliders')}
                                    </button>
                                )}
                            >
                                <div className="space-y-3">
                                    {visibleSliders.map((key) => (
                                        <SettingSlider
                                            key={key}
                                            id={`doc-cleanup-${key}`}
                                            label={t(`documentCleanupImage.sliders.${key}`)}
                                            value={adjustments[key]}
                                            disabled={working}
                                            onChange={(value) => {
                                                setAdjustment(key, value);
                                                setDownloaded(false);
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Twenty phone photos of one book are lit the same
                                    way, so the tuning that fixed page one fixes the
                                    rest. Crop and rotation stay per page — those
                                    are about how each shot was framed. */}
                                {pages.length > 1 ? (
                                    <ApplyToAllButton
                                        onClick={() => { applyToAll(); setDownloaded(false); }}
                                        disabled={working}
                                        className="mt-3"
                                    >
                                        {t('documentCleanupImage.applyToAll', {
                                            count: toBnDigits(pages.length - 1),
                                        })}
                                    </ApplyToAllButton>
                                ) : null}
                            </ToolCard>
                        ) : null}

                        <OutputFormatPicker
                            value={format}
                            onChange={setFormat}
                            formats={['jpg', 'png', 'webp', 'avif']}
                            disabled={working}
                        />
                    </div>
                )}
                preview={(
                    <ToolPreviewPanel
                        title={t('documentCleanupImage.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('documentCleanupImage.previewEmpty')}
                        idleHint={t('documentCleanupImage.previewEmptyHint')}
                        processingLabel={importing
                            ? t('documentCleanupImage.importing')
                            : t('documentCleanupImage.processing')}
                        error={error}
                        canvasTone="plain"
                        // The page stays visible through a frosted sheet while a
                        // crop or rotate lands, so the wait shows the work.
                        processingTone="veil"
                        showPasteHint
                        showIdle={!hasPages && !error}
                        onIdleClick={openPicker}
                        onIdleDrop={handleDrop}
                        onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onIdleDragLeave={() => setIsDragging(false)}
                        idleDragging={isDragging}
                    >
                        {hasPages ? (
                            <div
                                className="flex h-full min-h-0 w-full gap-3"
                                onDrop={handleDrop}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                            >
                                {/* The rail is part of the canvas, not the settings
                                    column: the pages ARE the document, and picking
                                    one is looking at it, not configuring it. */}
                                <div className="w-[86px] flex-none sm:w-[104px]">
                                    <DocumentCleanupPageRail
                                        pages={pages}
                                        selectedId={selectedId}
                                        onSelect={select}
                                        onRemove={removePage}
                                        onReorder={reorder}
                                        onAdd={openPicker}
                                        disabled={working || pages.length >= MAX_PAGES}
                                        labels={{
                                            add: t('documentCleanupImage.addPages'),
                                            remove: t('documentCleanupImage.removePage'),
                                            page: t('documentCleanupImage.page'),
                                            loading: t('documentCleanupImage.processing'),
                                        }}
                                    />
                                </div>
                                <div className="min-h-0 min-w-0 flex-1">
                                    <DocumentCleanupPreview
                                        imageData={previewResult}
                                        compareImageData={previewSource}
                                        compare={compare}
                                        labels={{
                                            cleaned: t('documentCleanupImage.cleanedBadge'),
                                            original: t('documentCleanupImage.originalBadge'),
                                            compare: t('documentCleanupImage.compare'),
                                        }}
                                        actions={previewActions}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </ToolPreviewPanel>
                )}
            />

            <DocumentCleanupCropModal
                opened={cropOpen}
                onClose={() => setCropOpen(false)}
                imageSrc={baseUrl}
                initialQuad={selected?.quad ?? null}
                fileName={selected?.name}
                onApply={(quad) => { setCropOpen(false); setDownloaded(false); applyCrop(quad); }}
                busy={working}
            />
        </>
    );
}
